import React from 'react'
import { Card, Typography, Button, Space } from 'antd'
import { UserOutlined, MedicineBoxOutlined, TeamOutlined, FileTextOutlined, FolderOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { CARD_STYLES, CARD_GRADIENTS } from '../utils/constants'
import coreReducer from '../../../../../../core/utils/reducer'
import CoreEnums from '../../../../../../core/utils/enums'
import EditPatientProfile from '../../../../../Admin/system-users/components/patient/EditPatient'
import systemUsersReducer from '../../../../../Admin/system-users/utils/reducer'
import store from '../../../../../../store'

const QuickLinksCard = () => {
  const dispatch = useDispatch()
  const authState = useSelector((state) => state.auth)

  // Get the current patient's data from auth state
  const currentPatientData = authState.agiliteUser
  const currentPatientId = currentPatientData?._id

  const setupPatientData = () => {
    if (!currentPatientData || !currentPatientId) return false

    try {
      // Get current active records from Redux store
      const activeRecords = store.getState().systemUsers.activeRecords
      const tmpActiveRecords = [...activeRecords]

      // Check if patient data already exists in activeRecords
      const recordIndex = tmpActiveRecords.findIndex((item) => item._id === currentPatientId)

      // If record doesn't exist, add it to activeRecords
      if (recordIndex === -1) {
        tmpActiveRecords.push(currentPatientData)
      } else {
        // Update the record if it already exists
        tmpActiveRecords[recordIndex] = currentPatientData
      }

      // Update activeRecords in Redux store
      dispatch(systemUsersReducer.actions.setActiveRecords(tmpActiveRecords))
      return true
    } catch (error) {
      console.error('Error setting up patient data:', error)
      return false
    }
  }

  const handleBackToDashboard = (currentTabKey) => {
    // Close the current tab which should return to the dashboard
    dispatch(coreReducer.actions.closeTab({ targetKey: currentTabKey }))
  }

  const handleDirectPatientFile = (targetTab, tabTitle = 'My Patient File') => {
    // Setup patient data and open EditPatientProfile directly
    const dataSetup = setupPatientData()
    if (!dataSetup) {
      console.error('Failed to setup patient data')
      return
    }

    const tabKey = `dashboard_patient_${targetTab}_${Date.now()}`

    dispatch(
      coreReducer.actions.addTab({
        key: tabKey,
        closable: true,
        label: tabTitle,
        children: (
          <EditPatientProfile
            dataId={currentPatientId}
            refreshView={() => handleBackToDashboard(tabKey)}
            handleRootTabKey={() => CoreEnums.tabKeys.HOME}
            isDependant={false}
            isExamination={false}
            isNew={false}
            initialTab={targetTab}
          />
        )
      })
    )
  }

  const handleProfileNavigation = () => {
    handleDirectPatientFile('patientProfile', 'Manage Profile')
  }

  const handlePrescriptionsNavigation = () => {
    handleDirectPatientFile('scripts', 'Manage Prescriptions')
  }

  const handleFamilyNavigation = () => {
    handleDirectPatientFile('dependants', 'Add Family Account')
  }

  const handleDocumentsNavigation = () => {
    handleDirectPatientFile('documents', 'View Medical Documents')
  }

  const handlePatientFileNavigation = () => {
    dispatch(
      coreReducer.actions.addTab({
        key: CoreEnums.tabKeys.MY_PATIENT_FILE,
        closable: true,
        label: CoreEnums.tabTitles.MY_PATIENT_FILE
      })
    )
  }

  const quickActionItems = [
    {
      key: 'profile',
      title: 'Manage Profile',
      subtitle: 'Update personal details',
      icon: <UserOutlined />,
      color: '#1890ff',
      backgroundColor: '#e6f7ff',
      borderColor: '#91d5ff',
      onClick: handleProfileNavigation
    },
    {
      key: 'prescriptions',
      title: 'Manage Prescriptions',
      subtitle: 'View medications & scripts',
      icon: <MedicineBoxOutlined />,
      color: '#722ed1',
      backgroundColor: '#f9f0ff',
      borderColor: '#d3adf7',
      onClick: handlePrescriptionsNavigation
    },
    {
      key: 'family',
      title: 'Add Family Account',
      subtitle: 'Manage dependants',
      icon: <TeamOutlined />,
      color: '#52c41a',
      backgroundColor: '#f6ffed',
      borderColor: '#b7eb8f',
      onClick: handleFamilyNavigation
    },
    {
      key: 'documents',
      title: 'Medical Documents',
      subtitle: 'View reports & files',
      icon: <FileTextOutlined />,
      color: '#fa8c16',
      backgroundColor: '#fff7e6',
      borderColor: '#ffd591',
      onClick: handleDocumentsNavigation
    },
    {
      key: 'patientFile',
      title: 'My Patient File',
      subtitle: 'Complete health record',
      icon: <FolderOutlined />,
      color: '#13c2c2',
      backgroundColor: '#e6fffb',
      borderColor: '#87e8de',
      onClick: handlePatientFileNavigation
    }
  ]

  return (
    <Card
      title='Quick Actions'
      bordered={false}
      style={{
        boxShadow: CARD_STYLES.shadow,
        borderRadius: CARD_STYLES.borderRadius
      }}
      headStyle={{
        background: CARD_GRADIENTS.orange,
        color: 'white',
        borderRadius: '12px 12px 0 0',
        fontWeight: '600'
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between' }}>
        {quickActionItems.map((item) => (
          <div
            key={item.key}
            onClick={item.onClick}
            style={{
              flex: '1 1 calc(20% - 16px)',
              minWidth: '160px',
              padding: '20px 16px',
              backgroundColor: item.backgroundColor,
              border: `2px solid ${item.borderColor}`,
              borderRadius: '12px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
              e.currentTarget.style.borderColor = item.color
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)'
              e.currentTarget.style.boxShadow = 'none'
              e.currentTarget.style.borderColor = item.borderColor
            }}
          >
            <div
              style={{
                fontSize: '32px',
                color: item.color,
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              {item.icon}
            </div>
            <div
              style={{
                fontWeight: '600',
                fontSize: '14px',
                color: '#262626',
                marginBottom: '4px'
              }}
            >
              {item.title}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#8c8c8c',
                lineHeight: '1.4'
              }}
            >
              {item.subtitle}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

export default QuickLinksCard
